import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearFileSession } from '@/lib/fileSession';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  FileText, 
  Calendar,
  MapPin,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Home,
  UserCircle,
  HelpCircle,
  CreditCard,
  PenTool,
  Globe,
  MessageSquare,
  ExternalLink,
  IdCard,
  GraduationCap,
  Settings,
  Headphones,
  Sparkles,
  Vote
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/NotificationBell';

const ROLE_LABELS = {
  super_admin: 'Super Administrateur',
  regional: 'Administrateur Régional',
  ophthalmologist: 'Ophtalmologiste',
  intern: 'Interne',
  tresorier: 'Trésorier',
  auteur: 'Auteur',
};

const UniversalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    clearFileSession();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const publicPages = [
    '/',
    '/auth',
    '/about',
    '/a-propos',
    '/partners',
    '/contact',
    '/faq',
    '/legal',
    '/privacy',
    '/cookies',
    '/adhesion',
    '/adhesion/courrier',
    '/adhesion/confirmation',
    '/patient-space',
    '/déclaration-incident',
    '/conditions',
    '/conditions-annuaire',
    '/support',
    '/maintenance',
    '/nos-formations',
    '/nos-replays',
    '/publications',
    '/bureau',
    '/forum',
  ];

  const publicPrefixes = [
    '/annuaire',
    '/patients',
    '/events',
    '/agenda',
    '/blog',
    '/region',
    '/bureau',
    '/formations-publiques',
    '/articles-publiques',
    '/linkedin',
    '/pages/',
    '/nos-formations/',
    '/publications/',
    '/forum/',
  ];

  const isPublicPage = publicPages.includes(location.pathname) || publicPrefixes.some(p => location.pathname.startsWith(p));
  const isAdminPage = location.pathname.startsWith('/admin');
  const isPatientPage = location.pathname.startsWith('/patients');

  // Si on est sur une page admin ou patients, ne pas wrapper avec UniversalLayout
  if (isAdminPage || isPatientPage) {
    return <>{children}</>;
  }

  if (!user || isPublicPage) {
    return <>{children}</>;
  }

  const isAdmin = user.role === 'super_admin' || user.role === 'regional';

  const getMenuItems = () => {
    const isAuthor = user?.author_status === 'approved';

    const items = [
      { title: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard', roles: ['all'] },
      { title: 'Mon Profil', icon: UserCircle, path: '/mon-profil', roles: ['all'] },
      { title: 'Mes Fiches', icon: IdCard, path: '/mes-fiches', roles: ['ophthalmologist'] },
      { title: 'Mes Adhésions', icon: CreditCard, path: '/mes-adhesions', roles: ['ophthalmologist', 'super_admin', 'regional', 'tresorier', 'auteur'] },
    ];

    if (isAuthor) {
      items.push(
        { title: 'Mes Articles', icon: FileText, path: '/mes-articles', roles: ['ophthalmologist'] },
        { title: 'Mes Formations', icon: GraduationCap, path: '/mes-formations-auteur', roles: ['ophthalmologist'] }
      );
    }

    items.push(
      { title: 'Copilote IA', icon: MessageSquare, path: '/copilot', roles: ['all'] },
      { title: 'Votes & Sondages', icon: Vote, path: '/votes', roles: ['all'] },
      { title: 'Messagerie', icon: Headphones, path: '/messagerie', roles: ['all'] },
      { title: 'Crédits IA', icon: Sparkles, path: '/credits', roles: ['all'] },
      { title: 'Événements', icon: Calendar, path: '/events', roles: ['all'] },
      { title: 'FAQ', icon: HelpCircle, path: '/faq', roles: ['all'] }
    );

    return items.filter(item => 
      item.roles.includes('all') || item.roles.includes(user.role)
    );
  };

  const menuItems = getMenuItems();

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col h-full bg-white/80 backdrop-blur-xl border-r border-gray-200/50 shadow-xl transition-all duration-300 ease-in-out ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Sidebar Header — Logo SFOALC entier déplié / avatar rond replié */}
        <div className={`flex items-center justify-between border-b border-gray-200/50 ${collapsed ? 'h-16 px-2' : 'h-16 px-4'}`}>
          {!collapsed ? (
            <>
              <img src="/SFOALC-logo-full.png" alt="SFOALC" className="h-6 w-auto" />
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronLeft size={18} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-full flex justify-center items-center gap-1 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <img src="/avatar-SFOALC.png" alt="SFOALC" className="w-9 h-9 rounded-full ring-2 ring-[#35AFE4]/20 shadow-sm" />
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30'
                    : 'text-gray-700 hover:bg-gray-100'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <Icon size={20} className="flex-shrink-0" />
                {!collapsed && <span className="truncate">{item.title}</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-3 border-t border-gray-200/50">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {!collapsed && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileOpen(false)}>
          <div className="fixed left-0 top-0 bottom-0 w-80 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between h-16 px-4 border-b">
              <div className="flex items-center gap-3">
                <img src="/avatar-SFOALC.png" alt="SFOALC" className="w-10 h-10 rounded-xl shadow-lg" />
                <div>
                  <h2 className="text-sm font-bold text-gray-900">SFOALC</h2>
                  <p className="text-xs text-gray-500 font-medium">Mon Espace</p>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2">
                <X size={24} />
              </button>
            </div>

            <div className="px-4 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                  <span className="text-sm font-bold text-white">{user.first_name?.[0]}{user.last_name?.[0]}</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{user.first_name} {user.last_name}</p>
                  <p className="text-xs text-gray-600">{ROLE_LABELS[user.role] || user.role}</p>
                </div>
              </div>
            </div>

            <nav className="overflow-y-auto px-3 py-4 space-y-1" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <button
                    key={index}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-sm font-medium ${
                      isActive
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.title}</span>
                  </button>
                );
              })}
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-3 border-t bg-white space-y-2">
              {isAdmin && (
                <button
                  onClick={() => {
                    navigate('/admin');
                    setMobileOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 shadow-lg"
                >
                  <Settings size={20} />
                  <span>Administration</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600"
              >
                <LogOut size={20} />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-xl font-extrabold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent tracking-tight">
              Mon Espace SFOALC
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 hover:text-[#1e3a5f] transition-colors border border-gray-200"
              data-testid="go-site-btn"
            >
              <Globe size={14} />
              <span>Voir le site</span>
            </button>
            <button
              onClick={() => navigate('/annuaire')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 hover:text-[#1e3a5f] transition-colors border border-gray-200"
              data-testid="go-annuaire-btn"
            >
              <MapPin size={14} />
              <span>Annuaire</span>
            </button>
            <NotificationBell />
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold text-sm shadow-lg hover:shadow-xl transition-all hover:scale-105"
              >
                <Settings size={18} />
                <span>Administration</span>
              </button>
            )}
            <div className="hidden md:flex items-center gap-3 pl-4 border-l border-gray-200">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-md ring-2 ring-white">
                <span className="text-sm font-bold text-white">{user.first_name?.[0]}{user.last_name?.[0]}</span>
              </div>
              <div className="hidden xl:block">
                <p className="text-sm font-extrabold text-gray-900 tracking-tight">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-600 font-semibold">{ROLE_LABELS[user.role] || user.role}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content - Scrollable */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default UniversalLayout;